/**
 * Sistema di export centralizzato per Masamune OS.
 *
 * Ogni entità esportabile ha una `ExportSource`:
 *   - endpoint  : URL API da cui fetchare (con pageSize alto per scaricare tutto)
 *   - columns   : lista delle colonne CSV con header leggibile + getter per nested
 *   - filename  : base del nome file ("clienti", "fatture", …)
 *
 * Il modale ExportModal usa questo registro per ogni "what" passato come payload.
 *
 * Differenze rispetto alla vecchia implementazione:
 * - I dati vengono fetchati FRESCHI (no cache TanStack) → sempre completi
 * - Colonne configurate per entità con denormalizzazione (es. client.legalName → "Cliente")
 * - Header leggibili in italiano (non field name tecnici)
 * - Date formattate come ISO YYYY-MM-DD
 * - Decimal/string corretti
 */
import { createElement } from 'react';
import { api } from './api';

export type CellValue = string | number | boolean | null | undefined;

export interface ExportColumn<T = any> {
  header: string;
  get: (row: T) => CellValue;
}

export interface ExportSource<T = any> {
  label: string;                          // mostrato in UI
  description: string;                    // help text in modal
  endpoint: string;                       // path API (senza prefix /api/v1)
  filenameBase: string;                   // "clienti" → masamune-clienti-2026-05-27.csv
  columns: ExportColumn<T>[];
  // Estrae array dal response (default: response.items)
  pickItems?: (resp: any) => T[];
}

// ────────────────────────────────────────────────────────────────
// Format helpers
// ────────────────────────────────────────────────────────────────
const dt = (v: any): string => {
  if (!v) return '';
  try { return new Date(v).toISOString().slice(0, 10); }
  catch { return String(v); }
};
const dtFull = (v: any): string => {
  if (!v) return '';
  try { return new Date(v).toISOString().replace('T', ' ').slice(0, 19); }
  catch { return String(v); }
};
const num = (v: any): number => v == null ? 0 : Number(v);
const yn = (v: any): string => v ? 'Sì' : 'No';

// ────────────────────────────────────────────────────────────────
// Sources registry — un mapping "what" → config di export
// ────────────────────────────────────────────────────────────────
export const EXPORT_SOURCES: Record<string, ExportSource> = {
  Clienti: {
    label: 'Clienti CRM',
    description: 'Anagrafiche complete: P.IVA, CF, SDI/PEC, indirizzo, account manager, stato.',
    endpoint: '/clients?pageSize=1000',
    filenameBase: 'clienti',
    columns: [
      { header: 'Ragione sociale', get: (r) => r.legalName },
      { header: 'Nome breve',      get: (r) => r.shortName },
      { header: 'Tipo',            get: (r) => r.type },
      { header: 'Stato',           get: (r) => r.status },
      { header: 'P.IVA',           get: (r) => r.vat },
      { header: 'CF',              get: (r) => r.taxCode },
      { header: 'Codice SDI',      get: (r) => r.sdiCode },
      { header: 'PEC',             get: (r) => r.pec },
      { header: 'Settore',         get: (r) => r.sector },
      { header: 'Indirizzo',       get: (r) => r.addressLine },
      { header: 'Città',           get: (r) => r.city },
      { header: 'CAP',             get: (r) => r.zip },
      { header: 'Provincia',       get: (r) => r.province },
      { header: 'Paese',           get: (r) => r.country },
      { header: 'Termini',         get: (r) => r.paymentTerms },
      { header: 'Account manager', get: (r) => r.accountManager ? `${r.accountManager.firstName} ${r.accountManager.lastName}` : '' },
      { header: 'Anno acquisizione', get: (r) => r.sinceYear },
      { header: 'Creato il',       get: (r) => dt(r.createdAt) },
    ],
  },

  Documenti: {
    label: 'Documenti (preventivi + fatture)',
    description: 'Tutti i documenti commerciali e fiscali con stato, totali e cliente.',
    endpoint: '/invoices?pageSize=1000',  // multi-fetch: vedi fetcher custom in modal
    filenameBase: 'documenti',
    columns: [],  // placeholder — il fetcher in modal compone preventivi+fatture (vedi sotto)
  },

  Fatture: {
    label: 'Fatture',
    description: 'Solo fatture (FATTURA/NC/Proforma/Avviso), con totali e date.',
    endpoint: '/invoices?pageSize=1000',
    filenameBase: 'fatture',
    columns: [
      { header: 'Numero',       get: (r) => r.number ?? '' },
      { header: 'Tipo',         get: (r) => r.type },
      { header: 'Stato',        get: (r) => r.status },
      { header: 'Cliente',      get: (r) => r.client?.legalName ?? '' },
      { header: 'P.IVA',        get: (r) => r.client?.vat ?? '' },
      { header: 'Progetto',     get: (r) => r.project?.code ?? '' },
      { header: 'Data emiss.',  get: (r) => dt(r.issueDate) },
      { header: 'Scadenza',     get: (r) => dt(r.dueDate) },
      { header: 'Data pagam.',  get: (r) => dt(r.paidDate) },
      { header: 'Imponibile',   get: (r) => num(r.subtotal) },
      { header: 'IVA',          get: (r) => num(r.vat) },
      { header: 'Totale',       get: (r) => num(r.total) },
      { header: 'Metodo pag.',  get: (r) => r.paymentMethod ?? '' },
      { header: 'FIC id',       get: (r) => r.ficDocumentId ?? '' },
    ],
  },

  Preventivi: {
    label: 'Preventivi',
    description: 'Tutti i preventivi con stato firma, validità e link cliente.',
    endpoint: '/quotes?pageSize=1000',
    filenameBase: 'preventivi',
    columns: [
      { header: 'Numero',       get: (r) => r.number ?? '' },
      { header: 'Titolo',       get: (r) => r.title ?? '' },
      { header: 'Stato',        get: (r) => r.status },
      { header: 'Cliente',      get: (r) => r.client?.legalName ?? '' },
      { header: 'Progetto',     get: (r) => r.project?.code ?? '' },
      { header: 'Emissione',    get: (r) => dt(r.issueDate) },
      { header: 'Valido fino a', get: (r) => dt(r.validUntil) },
      { header: 'Inviato il',   get: (r) => dt(r.sentAt) },
      { header: 'Visto il',     get: (r) => dt(r.viewedAt) },
      { header: 'Firmato il',   get: (r) => dtFull(r.signedAt) },
      { header: 'Firmato da',   get: (r) => r.signerName ?? '' },
      { header: 'Imponibile',   get: (r) => num(r.subtotal) },
      { header: 'IVA',          get: (r) => num(r.vat) },
      { header: 'Totale',       get: (r) => num(r.total) },
      { header: 'Convertito in fattura', get: (r) => r.convertedToInvoice?.number ?? '' },
    ],
  },

  Talent: {
    label: 'Candidati Talent',
    description: 'Anagrafiche candidati con stage funnel, skill, contatti.',
    endpoint: '/candidates?pageSize=1000',
    filenameBase: 'talent',
    columns: [
      { header: 'Nome',        get: (r) => r.firstName },
      { header: 'Cognome',     get: (r) => r.lastName },
      { header: 'Email',       get: (r) => r.email ?? '' },
      { header: 'Telefono',    get: (r) => r.phone ?? '' },
      { header: 'Città',       get: (r) => r.city ?? '' },
      { header: 'Stage',       get: (r) => r.stage },
      { header: 'Skill',       get: (r) => r.skill ?? '' },
      { header: 'Ricerca attiva', get: (r) => yn(r.activelyLooking) },
      { header: 'Salario atteso', get: (r) => num(r.expectedSalary) },
      { header: 'Creato il',   get: (r) => dt(r.createdAt) },
    ],
  },

  Professionisti: {
    label: 'Professionisti / fornitori',
    description: 'Anagrafiche professionisti con skill, città, stato.',
    endpoint: '/suppliers?pageSize=1000',
    filenameBase: 'professionisti',
    columns: [
      { header: 'Nome completo', get: (r) => r.fullName },
      { header: 'P.IVA',        get: (r) => r.vat ?? '' },
      { header: 'CF',           get: (r) => r.taxCode ?? '' },
      { header: 'Email',        get: (r) => r.email ?? '' },
      { header: 'Telefono',     get: (r) => r.phone ?? '' },
      { header: 'Città',        get: (r) => r.city ?? '' },
      { header: 'Provincia',    get: (r) => r.province ?? '' },
      { header: 'Tariffa/ora',  get: (r) => num(r.hourlyRate) },
      { header: 'Attivo',       get: (r) => yn(r.active) },
      { header: 'Skill',        get: (r) => Array.isArray(r.skills) ? r.skills.map((s: any) => s.name ?? s).join('; ') : '' },
      { header: 'Creato il',    get: (r) => dt(r.createdAt) },
    ],
  },

  Deliverables: {
    label: 'Deliverables',
    description: 'Output del team con progetto, stato, scadenza, assegnatario.',
    endpoint: '/deliverables?pageSize=1000',
    filenameBase: 'deliverables',
    columns: [
      { header: 'Codice',      get: (r) => r.code ?? '' },
      { header: 'Titolo',      get: (r) => r.title },
      { header: 'Tipo',        get: (r) => r.type ?? '' },
      { header: 'Stato',       get: (r) => r.status },
      { header: 'Progetto',    get: (r) => r.project?.code ?? '' },
      { header: 'Cliente',     get: (r) => r.project?.client?.shortName ?? '' },
      { header: 'Assegnato a', get: (r) => r.assignee ? `${r.assignee.firstName} ${r.assignee.lastName}` : '' },
      { header: 'Scadenza',    get: (r) => dt(r.dueDate) },
      { header: 'Creato il',   get: (r) => dt(r.createdAt) },
    ],
  },

  Progetti: {
    label: 'Progetti',
    description: 'Tutti i progetti con divisione, status, ricavi, PM.',
    endpoint: '/projects?pageSize=1000',
    filenameBase: 'progetti',
    columns: [
      { header: 'Codice',     get: (r) => r.code },
      { header: 'Nome',       get: (r) => r.name },
      { header: 'Cliente',    get: (r) => r.client?.legalName ?? '' },
      { header: 'Divisione',  get: (r) => r.division },
      { header: 'Modello',    get: (r) => r.model },
      { header: 'Stato',      get: (r) => r.status },
      { header: 'PM',         get: (r) => r.pm ? `${r.pm.firstName} ${r.pm.lastName}` : '' },
      { header: 'Start',      get: (r) => dt(r.startDate) },
      { header: 'Deadline',   get: (r) => dt(r.deadline) },
      { header: 'Ricavi',     get: (r) => num(r.revenue) },
      { header: 'Costi',      get: (r) => num(r.cost) },
      { header: 'Progresso %', get: (r) => num(r.progress) },
    ],
  },

  'Audit log': {
    label: 'Audit log Vault',
    description: 'Tracciamento accessi alle credenziali per compliance.',
    endpoint: '/vault/access-log?pageSize=2000',
    filenameBase: 'vault-audit',
    columns: [
      { header: 'Quando',     get: (r) => dtFull(r.createdAt) },
      { header: 'Azione',     get: (r) => r.action },
      { header: 'Utente',     get: (r) => r.user ? `${r.user.firstName} ${r.user.lastName}` : '' },
      { header: 'Credenziale', get: (r) => r.entry?.label ?? '' },
      { header: 'IP',         get: (r) => r.ip ?? '' },
    ],
    pickItems: (r) => Array.isArray(r) ? r : (r.items ?? []),
  },

  Servizi: {
    label: 'Servizi gestiti (abbonamenti)',
    description: 'Hosting/abbonamenti clienti con stato, ciclo, prezzo.',
    endpoint: '/services?pageSize=1000',
    filenameBase: 'servizi',
    columns: [
      { header: 'Cliente',     get: (r) => r.client?.legalName ?? '' },
      { header: 'Servizio',    get: (r) => r.name },
      { header: 'Tipo',        get: (r) => r.type },
      { header: 'Stato',       get: (r) => r.status },
      { header: 'Provider',    get: (r) => r.provider },
      { header: 'Ciclo',       get: (r) => r.billingCycle },
      { header: 'Importo',     get: (r) => num(r.amount) },
      { header: 'Valuta',      get: (r) => r.currency ?? 'EUR' },
      { header: 'Prossimo rinnovo', get: (r) => dt(r.nextRenewalAt) },
      { header: 'Cancellato il', get: (r) => dt(r.cancelledAt) },
    ],
  },
};

// ────────────────────────────────────────────────────────────────
// Builders
// ────────────────────────────────────────────────────────────────
export function buildCSV<T>(rows: T[], columns: ExportColumn<T>[]): string {
  const escape = (v: CellValue): string => {
    if (v == null) return '';
    const s = typeof v === 'string' ? v : String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes(';')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const headers = columns.map((c) => c.header);
  const lines = [
    headers.join(','),
    ...rows.map((r) => columns.map((c) => escape(c.get(r))).join(',')),
  ];
  // BOM UTF-8 per Excel italiano (riconosce gli accenti)
  return '﻿' + lines.join('\r\n');
}

export function buildJSON<T>(rows: T[], columns: ExportColumn<T>[]): string {
  // JSON con stesso shape del CSV (key = header)
  const data = rows.map((r) => {
    const obj: Record<string, CellValue> = {};
    for (const c of columns) obj[c.header] = c.get(r);
    return obj;
  });
  return JSON.stringify(data, null, 2);
}

export function downloadFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime + ';charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ────────────────────────────────────────────────────────────────
// Auto-paginazione client-side.
// Il backend ha pageSize.max=200 — per recuperare TUTTI i record fetchiamo
// pagina per pagina finché non finisce (max 50 pagine = 10.000 record, oltre
// è ragionevolmente un esport pesante che merita un job dedicato in futuro).
// ────────────────────────────────────────────────────────────────
async function fetchAllPaginated(baseEndpoint: string, pickItems?: (r: any) => any[]): Promise<any[]> {
  const all: any[] = [];
  const sep = baseEndpoint.includes('?') ? '&' : '?';
  let page = 1;
  const PAGE_SIZE = 200;
  const MAX_PAGES = 50;

  while (page <= MAX_PAGES) {
    const endpoint = `${baseEndpoint}${sep}pageSize=${PAGE_SIZE}&page=${page}`;
    const resp = await api.get<any>(endpoint);
    const items = pickItems ? pickItems(resp) : (resp.items ?? resp);
    if (!Array.isArray(items) || items.length === 0) break;
    all.push(...items);
    // Se il response include totalPages e l'abbiamo raggiunto, stop
    const tp = resp.totalPages;
    if (typeof tp === 'number' && page >= tp) break;
    // Se il backend non ritorna pagination metadata, ci fermiamo quando
    // la pagina ha meno record di PAGE_SIZE (ultima pagina parziale)
    if (items.length < PAGE_SIZE) break;
    page++;
  }
  return all;
}

/**
 * Strippa il `?pageSize=...&page=...` dall'endpoint configurato — la nostra
 * auto-paginazione lo aggiunge da sola.
 */
function stripPagination(endpoint: string): string {
  return endpoint.replace(/[?&](pageSize|page)=\d+/g, '').replace(/\?$/, '');
}

export type TableFormat = 'csv' | 'json' | 'pdf';

/**
 * Fetcha + denormalizza + scarica il file.
 *
 * Casi speciali:
 *  - "Documenti" → fetcha sia /invoices che /quotes e li unifica
 *  - format='pdf' → usa GenericListPdf con auto-paginazione
 */
export async function runExport(what: string, format: TableFormat): Promise<{ count: number; filename: string }> {
  const today = new Date().toISOString().slice(0, 10);

  // Caso speciale: "Documenti" = preventivi + fatture insieme
  if (what === 'Documenti') {
    const [invItems, qItems] = await Promise.all([
      fetchAllPaginated('/invoices'),
      fetchAllPaginated('/quotes'),
    ]);
    type Row = {
      kind: 'Preventivo' | 'Fattura';
      number: string;
      type: string;
      status: string;
      client: string;
      project: string;
      issueDate: string;
      dueDate: string;
      total: number;
    };
    const rows: Row[] = [
      ...qItems.map((q: any): Row => ({
        kind: 'Preventivo',
        number: q.number ?? '',
        type: q.title ?? 'Proposta',
        status: q.status,
        client: q.client?.legalName ?? '',
        project: q.project?.code ?? '',
        issueDate: dt(q.issueDate),
        dueDate: dt(q.validUntil),
        total: num(q.total),
      })),
      ...invItems.map((i: any): Row => ({
        kind: 'Fattura',
        number: i.number ?? '',
        type: i.type,
        status: i.status,
        client: i.client?.legalName ?? '',
        project: i.project?.code ?? '',
        issueDate: dt(i.issueDate),
        dueDate: dt(i.dueDate),
        total: num(i.total),
      })),
    ].sort((a, b) => b.issueDate.localeCompare(a.issueDate));

    const cols: ExportColumn<Row>[] = [
      { header: 'Tipo doc',  get: (r) => r.kind },
      { header: 'Numero',    get: (r) => r.number },
      { header: 'Sotto-tipo', get: (r) => r.type },
      { header: 'Stato',     get: (r) => r.status },
      { header: 'Cliente',   get: (r) => r.client },
      { header: 'Progetto',  get: (r) => r.project },
      { header: 'Emissione', get: (r) => r.issueDate },
      { header: 'Scadenza',  get: (r) => r.dueDate },
      { header: 'Totale',    get: (r) => r.total },
    ];
    const filename = `masamune-documenti-${today}.${format}`;
    if (format === 'pdf') {
      await generatePdfList({ filenameBase: 'documenti', title: 'Documenti contabili', kicker: 'Contabilità · Documenti', rows, columns: cols });
      return { count: rows.length, filename: `masamune-documenti-${today}.pdf` };
    }
    const content = format === 'csv' ? buildCSV(rows, cols) : buildJSON(rows, cols);
    downloadFile(filename, content, format === 'csv' ? 'text/csv' : 'application/json');
    return { count: rows.length, filename };
  }

  const source = EXPORT_SOURCES[what];
  if (!source) throw new Error(`Export non configurato per "${what}"`);

  // Auto-paginazione: scarichiamo tutte le pagine in modo trasparente
  const items = await fetchAllPaginated(stripPagination(source.endpoint), source.pickItems);

  if (!Array.isArray(items)) throw new Error('Risposta API in formato inatteso');

  if (format === 'pdf') {
    await generatePdfList({
      filenameBase: source.filenameBase,
      title: source.label,
      kicker: `Masamune OS · ${source.label}`,
      rows: items,
      columns: source.columns,
    });
    return { count: items.length, filename: `masamune-${source.filenameBase}-${today}.pdf` };
  }

  const filename = `masamune-${source.filenameBase}-${today}.${format}`;
  const content = format === 'csv' ? buildCSV(items, source.columns) : buildJSON(items, source.columns);
  downloadFile(filename, content, format === 'csv' ? 'text/csv' : 'application/json');
  return { count: items.length, filename };
}

// ────────────────────────────────────────────────────────────────
// PDF list generator (lazy import della lib)
// ────────────────────────────────────────────────────────────────
async function generatePdfList(opts: {
  filenameBase: string;
  title: string;
  kicker: string;
  rows: any[];
  columns: ExportColumn<any>[];
}): Promise<void> {
  const [{ pdf }, { GenericListPdf }, orgResp] = await Promise.all([
    import('@react-pdf/renderer'),
    import('./pdf/GenericListPdf'),
    api.get<any>('/settings/org').catch(() => ({ organizationName: 'Masamune' })),
  ]);
  const today = new Date().toISOString().slice(0, 10);
  const doc = createElement(GenericListPdf, {
    organization: { name: orgResp.organizationName ?? 'Masamune' },
    title: opts.title,
    kicker: opts.kicker,
    rows: opts.rows,
    columns: opts.columns,
  });
  const blob = await pdf(doc as any).toBlob();
  const filename = `masamune-${opts.filenameBase}-${today}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ────────────────────────────────────────────────────────────────
// Dashboard export — caso speciale che usa @react-pdf/renderer.
// La libreria è lazy-loaded per non pesare sul bundle iniziale.
// ────────────────────────────────────────────────────────────────

export type DashboardExportFormat = 'pdf' | 'csv' | 'json';

interface FetchedDashboard {
  overview: {
    kpis: {
      revenueYTD: number;
      activeProjects: number;
      draftProjects: number;
      pipelineCandidates: number;
      openInvoicesAmount: number;
      openInvoicesCount: number;
      marginPct?: number;
    };
    divisionRevenue: Array<{ division: string; revenue: number; cost?: number; marginPct?: number }>;
    projects: Array<{ code: string; name: string; client: string; division: string; progress: number; pm: { firstName: string; lastName: string } | null; deadline: string | null; riskLevel: string }>;
    alerts: Array<{ message: string; project: { code: string; name: string } | null }>;
  };
  organizationName: string;
  period: { q: number; y: number };
  showMargin: boolean;
}

async function fetchDashboardForExport(period: { q: number; y: number }): Promise<FetchedDashboard> {
  const [overview, org, me] = await Promise.all([
    api.get<any>(`/dashboard/overview?q=${period.q}&y=${period.y}`),
    api.get<any>('/settings/org').catch(() => ({ organizationName: 'Masamune' })),
    api.get<any>('/auth/me').catch(() => ({ user: { role: { canSeeMargin: false } } })),
  ]);
  return {
    overview: {
      kpis: overview.kpis ?? {},
      divisionRevenue: overview.divisionRevenue ?? [],
      projects: overview.projects ?? [],
      alerts: overview.alerts ?? [],
    },
    organizationName: org.organizationName ?? 'Masamune',
    period,
    showMargin: !!me.user?.role?.canSeeMargin,
  };
}

// ────────────────────────────────────────────────────────────────
// Report export — rispetta il PERIODO selezionato nella pagina Report
// (prima il pulsante PDF esportava per errore lo snapshot dashboard del
// trimestre corrente, ignorando periodo e dati del report). Esporta i dati
// reali su schermo: ricavi/margine per divisione, top clienti, fatturato
// mensile — per il periodo scelto.
// ────────────────────────────────────────────────────────────────
export interface ReportPeriod { key: string; year: number; label?: string }

interface ReportRow { Sezione: string; Voce: string; Valore: number; Note: string }

export async function runReportExport(
  format: DashboardExportFormat,
  period: ReportPeriod,
): Promise<{ filename: string; count: number }> {
  const today = new Date().toISOString().slice(0, 10);
  const qs = `period=${period.key}&year=${period.year}`;
  const slug = `${period.key}-${period.year}`;
  const label = period.label ?? `${period.key.toUpperCase()} ${period.year}`;
  const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

  const [divisions, clientsTop, monthly, org] = await Promise.all([
    api.get<any[]>(`/reports/divisions?${qs}`).catch(() => []),
    api.get<any[]>(`/reports/clients-top?${qs}`).catch(() => []),
    api.get<any[]>(`/reports/monthly?${qs}`).catch(() => []),
    api.get<any>('/settings/org').catch(() => ({ organizationName: 'Masamune' })),
  ]);

  const rows: ReportRow[] = [
    ...(divisions ?? []).map((d): ReportRow => ({
      Sezione: 'Divisione',
      Voce: String(d.division),
      Valore: num(d.revenue),
      Note: d.marginPct != null ? `margine ${Number(d.marginPct).toFixed(1)}%` : '',
    })),
    ...(clientsTop ?? []).map((c): ReportRow => ({
      Sezione: 'Cliente top',
      Voce: c.legalName ?? '',
      Valore: num(c.revenue),
      Note: c.marginPct != null ? `margine ${Number(c.marginPct).toFixed(1)}%` : '',
    })),
    ...(monthly ?? []).map((m): ReportRow => ({
      Sezione: 'Fatturato mensile',
      Voce: `${MONTHS[(m.month ?? 1) - 1] ?? m.month} ${m.year ?? period.year}`,
      Valore: num(m.total),
      Note: '',
    })),
  ];

  const cols: ExportColumn<ReportRow>[] = [
    { header: 'Sezione', get: (r) => r.Sezione },
    { header: 'Voce',    get: (r) => r.Voce },
    { header: 'Valore (€)', get: (r) => r.Valore },
    { header: 'Note',    get: (r) => r.Note },
  ];

  if (format === 'pdf') {
    await generatePdfList({
      filenameBase: `report-${slug}`,
      title: `Report & analytics — ${label}`,
      kicker: `${org.organizationName ?? 'Masamune'} · Report`,
      rows,
      columns: cols,
    });
    return { count: rows.length, filename: `masamune-report-${slug}-${today}.pdf` };
  }

  const filename = `masamune-report-${slug}-${today}.${format}`;
  const content = format === 'csv' ? buildCSV(rows, cols) : buildJSON(rows, cols);
  downloadFile(filename, content, format === 'csv' ? 'text/csv' : 'application/json');
  return { count: rows.length, filename };
}

export async function runDashboardExport(
  format: DashboardExportFormat,
  period: { q: number; y: number },
): Promise<{ filename: string; count: number }> {
  const today = new Date().toISOString().slice(0, 10);
  const data = await fetchDashboardForExport(period);

  if (format === 'pdf') {
    // Lazy import: la lib PDF (~300KB) viene scaricata solo quando l'utente
    // clicca "Esporta PDF" → no impatto sul bundle iniziale.
    const [{ pdf }, { DashboardPdf }] = await Promise.all([
      import('@react-pdf/renderer'),
      import('./pdf/DashboardPdf'),
    ]);
    const doc = createElement(DashboardPdf, {
      data: {
        organization: { name: data.organizationName },
        period: data.period,
        showMargin: data.showMargin,
        kpis: data.overview.kpis,
        divisionRevenue: data.overview.divisionRevenue,
        projects: data.overview.projects,
        alerts: data.overview.alerts,
      },
    });
    const blob = await pdf(doc as any).toBlob();
    const filename = `masamune-dashboard-Q${period.q}-${period.y}-${today}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return { filename, count: 1 };
  }

  // CSV / JSON: aggreghiamo i KPI in un formato tabellare denormalizzato
  const rows = [
    { Metrica: 'Fatturato YTD',          Valore: data.overview.kpis.revenueYTD,         Note: 'EUR · anno corrente' },
    { Metrica: 'Da incassare',           Valore: data.overview.kpis.openInvoicesAmount, Note: `${data.overview.kpis.openInvoicesCount} fatture aperte` },
    { Metrica: 'Progetti attivi',        Valore: data.overview.kpis.activeProjects,     Note: `${data.overview.kpis.draftProjects} in bozza` },
    { Metrica: 'Pipeline candidati',     Valore: data.overview.kpis.pipelineCandidates, Note: 'in funnel' },
    ...(data.showMargin && data.overview.kpis.marginPct != null
      ? [{ Metrica: 'Margine %', Valore: data.overview.kpis.marginPct, Note: 'soglia 20%' }]
      : []),
    ...data.overview.divisionRevenue.map((d) => ({
      Metrica: `Ricavi ${d.division}`,
      Valore: d.revenue,
      Note: d.marginPct != null ? `margine ${d.marginPct.toFixed(1)}%` : '',
    })),
  ];

  const cols: ExportColumn<typeof rows[number]>[] = [
    { header: 'Metrica', get: (r) => r.Metrica },
    { header: 'Valore',  get: (r) => r.Valore },
    { header: 'Note',    get: (r) => r.Note },
  ];

  const filename = `masamune-dashboard-Q${period.q}-${period.y}-${today}.${format}`;
  const content = format === 'csv' ? buildCSV(rows, cols) : buildJSON(rows, cols);
  downloadFile(filename, content, format === 'csv' ? 'text/csv' : 'application/json');
  return { filename, count: rows.length };
}
